// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Post-login onboarding  (/welcome/profile → terms → delivery → consent → aboard)
// Step 2 of 5 — collects name, nickname, photo and an optional referral code, then saves it via
// PUT /api/me/profile (utils/profile.ts). Reachable only right after a login (`fromLogin` state).
// Next: /welcome/terms (Legal & Freshness Charter), then delivery location, then message preferences.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Camera,
  CircleAlert,
  CircleCheck,
  Copy,
  Gift,
  IdCard,
  Images,
  Loader2,
  Lock,
  Phone,
  ShieldCheck,
  Smile,
  User,
  X,
} from "lucide-react";
import PageTransition from "../components/PageTransition";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ApiError } from "../utils/api";
import { AvatarError, fileToAvatarDataUrl } from "../utils/avatarImage";
import { pendingReferral } from "../utils/referrals";
import { NAME_MAX, NAME_MIN, REFERRAL_PATTERN, fetchProfile, saveProfile, type Profile } from "../utils/profile";

const FIELD =
  "h-12 w-full rounded-lg bg-ocean-700/5 pl-10 pr-10 text-base text-ocean-950 outline-none transition-colors placeholder:text-ocean-800/50 focus:bg-ocean-700/10 focus:ring-2 focus:ring-teal-600 dark:bg-white/10 dark:text-foam-50 dark:placeholder:text-foam-100/50";

function formatPhone(e164: string | null): string {
  const digits = (e164 ?? "").replace(/\D/g, "");
  return digits.length === 12 && digits.startsWith("91")
    ? `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`
    : (e164 ?? "");
}

type Load = { status: "loading" } | { status: "error" } | { status: "ready"; profile: Profile };

/**
 * Step 2 of 3 of the post-login flow (profile → consent → welcome). Only reachable straight
 * after a mobile sign-in; everything is optional except a name, and it can be skipped.
 */
export default function ProfileSetup() {
  useDocumentTitle("Profile setup");
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const arrivedFromLogin = (location.state as { fromLogin?: boolean } | null)?.fromLogin === true;

  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [fullName, setFullName] = useState("");
  const [nickname, setNickname] = useState("");
  const [photo, setPhoto] = useState<string | null>(null);
  // A friend who arrived through an invite link (…/welcome?ref=CODE) starts with that code ready; they can remove or change it.
  const [referralInput, setReferralInput] = useState(() => pendingReferral());
  const [referralReady, setReferralReady] = useState(() => pendingReferral() !== "");
  const [referralError, setReferralError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const galleryRef = useRef<HTMLInputElement>(null);
  const selfieRef = useRef<HTMLInputElement>(null);
  const signedIn = auth.status === "signed-in";

  const onward = () => navigate("/welcome/terms", { state: { fromLogin: true }, replace: true });

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    fetchProfile().then(
      (profile) => {
        if (cancelled) return;
        // A returning customer who finished their profile earlier isn't asked again.
        if (profile.completed) {
          navigate("/welcome/terms", { state: { fromLogin: true }, replace: true });
          return;
        }
        setFullName(profile.fullName ?? "");
        setNickname(profile.nickname ?? "");
        setPhoto(profile.photoDataUrl);
        setLoad({ status: "ready", profile });
      },
      () => !cancelled && setLoad({ status: "error" }),
    );
    return () => {
      cancelled = true;
    };
  }, [signedIn, navigate]);

  if (!arrivedFromLogin || auth.status === "signed-out") return <Navigate to="/" replace />;

  const trimmedName = fullName.trim();
  const nameValid = trimmedName.length >= NAME_MIN && trimmedName.length <= NAME_MAX;
  const nameError = nameTouched && !nameValid ? `Enter your name (${NAME_MIN}–${NAME_MAX} characters).` : null;

  const pickPhoto = async (file: File | undefined) => {
    if (!file) return;
    setPhotoError(null);
    try {
      setPhoto(await fileToAvatarDataUrl(file));
    } catch (caught) {
      setPhotoError(caught instanceof AvatarError ? caught.message : "We couldn't use that photo.");
    }
  };

  const applyReferral = () => {
    const code = referralInput.trim();
    if (!REFERRAL_PATTERN.test(code)) {
      setReferralReady(false);
      setReferralError("Codes are 4–20 letters and numbers, with no spaces.");
      return;
    }
    setReferralError(null);
    setReferralReady(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setNameTouched(true);
    if (!nameValid) {
      document.getElementById("profile-name")?.focus();
      return;
    }
    setSaving(true);
    setFormError(null);
    setReferralError(null);
    try {
      await saveProfile({
        fullName: trimmedName,
        nickname: nickname.trim() || undefined,
        photoDataUrl: photo,
        referralCode: referralReady ? referralInput.trim() : undefined,
      });
      onward();
    } catch (caught) {
      setSaving(false);
      if (caught instanceof ApiError && (caught.code === "REFERRAL_INVALID" || caught.code === "REFERRAL_ALREADY_SET")) {
        setReferralReady(false);
        setReferralError(caught.message);
        document.getElementById("profile-referral")?.focus();
      } else {
        setFormError(caught instanceof ApiError ? caught.message : "We couldn't save your profile. Please try again.");
      }
    }
  };

  const copyOwnCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: the code is on screen to read out.
    }
  };

  const ownCode = load.status === "ready" ? load.profile.referralCode : null;

  return (
    <PageTransition>
      <div className="min-h-screen bg-foam-50 pt-8 text-ocean-950 dark:bg-ocean-950 dark:text-foam-50">
        <header className="sticky top-8 z-40 bg-foam-50/90 shadow-[0_1px_8px_rgba(0,0,0,0.04)] backdrop-blur-xl dark:bg-ocean-950/90">
          <div className="mx-auto flex h-16 max-w-xl items-center justify-between gap-2 px-4">
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Go back"
                onClick={() => navigate("/welcome")}
                className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-teal-600"
              >
                <ArrowLeft className="h-6 w-6" aria-hidden="true" />
              </button>
              <img src="/logo.png" alt="" className="h-8 w-8 object-contain" />
            </div>
            <div className="text-center">
              <p className="text-[11px] font-bold uppercase tracking-wider text-teal-700 dark:text-teal-300">Step 2 of 3</p>
              <p className="font-display text-lg font-semibold leading-tight">Profile setup</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={onward}
                className="flex h-11 items-center rounded-lg px-2 text-sm font-semibold text-ocean-800 hover:text-teal-700 focus-visible:outline-2 focus-visible:outline-teal-600 dark:text-foam-100"
              >
                Skip
              </button>
              <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-700 text-white">
                <User className="h-4 w-4" />
              </span>
            </div>
          </div>
        </header>

        {load.status === "loading" && (
          <p role="status" className="mx-auto mt-24 flex max-w-xl items-center justify-center gap-2 text-ocean-800 dark:text-foam-100">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            Getting your profile…
          </p>
        )}

        {load.status === "error" && (
          <div className="mx-auto mt-16 max-w-xl px-4 text-center" role="alert">
            <p className="font-semibold">We couldn&apos;t load your profile.</p>
            <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">Check your connection, or skip for now and finish later.</p>
            <button
              type="button"
              onClick={onward}
              className="mt-5 inline-flex min-h-11 items-center rounded-full bg-teal-700 px-6 font-bold text-white hover:bg-teal-600"
            >
              Continue without profile
            </button>
          </div>
        )}

        {load.status === "ready" && (
          <form onSubmit={submit} noValidate className="mx-auto flex max-w-xl flex-col gap-6 px-4 pb-44 pt-6">
            <section className="text-center">
              <p className="inline-flex items-center gap-1.5 rounded-full bg-ocean-700/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-ocean-800 dark:bg-white/10 dark:text-foam-100">
                <BadgeCheck className="h-4 w-4" aria-hidden="true" />
                Harbour onboarding
              </p>
              <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Complete your profile</h1>
              <p className="mx-auto mt-1.5 max-w-sm text-sm text-ocean-800/85 dark:text-foam-100/80">
                Tell us your name and add a photo so our delivery team knows who they&apos;re delivering to.
              </p>
            </section>

            <section className="flex flex-col items-center" aria-label="Profile photo">
              <div className="relative">
                <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full bg-ocean-700/10 dark:bg-white/10">
                  {photo ? (
                    <img src={photo} alt="Your profile photo" className="h-full w-full object-cover" />
                  ) : (
                    <span className="flex flex-col items-center text-teal-700 dark:text-teal-300">
                      <User className="h-9 w-9" aria-hidden="true" />
                      <span className="mt-0.5 text-[11px] font-semibold">No photo yet</span>
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Choose a photo"
                  onClick={() => galleryRef.current?.click()}
                  className="absolute bottom-0 right-0 flex h-9 w-9 items-center justify-center rounded-full bg-teal-700 text-white shadow-md hover:bg-teal-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                >
                  <Camera className="h-[18px] w-[18px]" aria-hidden="true" />
                </button>
              </div>
              <input ref={selfieRef} type="file" accept="image/*" capture="user" hidden onChange={(e) => { void pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { void pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => selfieRef.current?.click()}
                  className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-ocean-700/10 px-4 text-sm font-semibold text-teal-700 hover:bg-ocean-700/15 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-white/10 dark:text-teal-300"
                >
                  <Camera className="h-[18px] w-[18px]" aria-hidden="true" />
                  Take selfie
                </button>
                <button
                  type="button"
                  onClick={() => galleryRef.current?.click()}
                  className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-ocean-700/10 px-4 text-sm font-semibold hover:bg-ocean-700/15 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-white/10"
                >
                  <Images className="h-[18px] w-[18px]" aria-hidden="true" />
                  Choose from gallery
                </button>
              </div>
              {photo && (
                <button type="button" onClick={() => setPhoto(null)} className="mt-2 text-xs font-semibold underline underline-offset-2">
                  Remove photo
                </button>
              )}
              <p className="mt-2.5 flex max-w-xs items-start justify-center gap-1 text-center text-xs text-ocean-800/80 dark:text-foam-100/70">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                JPG, PNG or WebP up to 5 MB • Shrunk on your device and used only for your account
              </p>
              <p role="alert" className="mt-1 text-center text-sm text-coral-600 dark:text-coral-300">{photoError}</p>
            </section>

            <section className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-sm dark:bg-ocean-900" aria-labelledby="details-title">
              <h2 id="details-title" className="flex items-center gap-2 font-display text-lg font-semibold">
                <IdCard className="h-5 w-5 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                Your details
              </h2>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <label htmlFor="profile-name">
                    Full name <span aria-hidden="true" className="text-coral-500">*</span>
                    <span className="sr-only"> (required)</span>
                  </label>
                  {nameValid && (
                    <span className="flex items-center gap-1 text-teal-700 dark:text-teal-300">
                      <CircleCheck className="h-3.5 w-3.5" aria-hidden="true" /> Looks good
                    </span>
                  )}
                </div>
                <div className="relative flex items-center">
                  <User className="pointer-events-none absolute left-3 h-5 w-5 text-ocean-800/70 dark:text-foam-100/70" aria-hidden="true" />
                  <input
                    id="profile-name"
                    value={fullName}
                    maxLength={NAME_MAX}
                    autoComplete="name"
                    placeholder="e.g. Rahul Sharma"
                    aria-invalid={nameError ? true : undefined}
                    aria-describedby="profile-name-hint profile-name-error"
                    onChange={(e) => setFullName(e.target.value)}
                    onBlur={() => setNameTouched(true)}
                    className={FIELD}
                  />
                  {fullName && (
                    <button
                      type="button"
                      aria-label="Clear name"
                      onClick={() => {
                        setFullName("");
                        document.getElementById("profile-name")?.focus();
                      }}
                      className="absolute right-2 flex h-8 w-8 items-center justify-center rounded-full text-ocean-800/70 hover:text-ocean-950 dark:text-foam-100/70"
                    >
                      <X className="h-[18px] w-[18px]" aria-hidden="true" />
                    </button>
                  )}
                </div>
                <p id="profile-name-hint" className="text-xs text-ocean-800/80 dark:text-foam-100/70">
                  Used for delivery notifications and your tax invoice.
                </p>
                <p id="profile-name-error" role="alert" className="flex items-center gap-1 text-sm text-coral-600 dark:text-coral-300">
                  {nameError && <CircleAlert className="h-4 w-4" aria-hidden="true" />}
                  {nameError}
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <label htmlFor="profile-nickname">Preferred name / nickname</label>
                  <span className="text-ocean-800/80 dark:text-foam-100/70">Optional</span>
                </div>
                <div className="relative flex items-center">
                  <Smile className="pointer-events-none absolute left-3 h-5 w-5 text-ocean-800/70 dark:text-foam-100/70" aria-hidden="true" />
                  <input
                    id="profile-nickname"
                    value={nickname}
                    maxLength={40}
                    autoComplete="nickname"
                    placeholder="e.g. Rahul"
                    onChange={(e) => setNickname(e.target.value)}
                    className={FIELD}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <span id="profile-mobile-label">Registered mobile</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-[11px] font-bold text-teal-700">
                    <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
                    Verified
                  </span>
                </div>
                <div className="flex h-12 items-center justify-between rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" aria-labelledby="profile-mobile-label" role="group">
                  <span className="flex items-center gap-2 font-semibold tracking-wide">
                    <Phone className="h-5 w-5 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                    {formatPhone(auth.user?.phone ?? null)}
                  </span>
                  <Lock className="h-[18px] w-[18px] text-ocean-800/70 dark:text-foam-100/70" aria-hidden="true" />
                  <span className="sr-only">Locked: this is the number you signed in with</span>
                </div>
              </div>
            </section>

            <section className="flex flex-col gap-3 rounded-xl bg-teal-100/60 p-4 dark:bg-teal-700/20" aria-labelledby="referral-title">
              <div className="flex items-start gap-3">
                <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-200 text-teal-700">
                  <Gift className="h-[22px] w-[22px]" />
                </span>
                <div>
                  <h2 id="referral-title" className="font-display text-lg font-semibold leading-snug">Have a referral code?</h2>
                  <p className="mt-0.5 text-xs text-ocean-800/85 dark:text-foam-100/80">
                    Enter a friend&apos;s code. We record it on your account; any reward is confirmed after your first delivery.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <label htmlFor="profile-referral" className="sr-only">Referral code</label>
                  <input
                    id="profile-referral"
                    value={referralInput}
                    maxLength={20}
                    autoCapitalize="characters"
                    autoComplete="off"
                    placeholder="ZENXXXXXX"
                    readOnly={referralReady}
                    aria-invalid={referralError ? true : undefined}
                    aria-describedby="profile-referral-message"
                    onChange={(e) => {
                      setReferralInput(e.target.value);
                      setReferralReady(false);
                      setReferralError(null);
                    }}
                    className="h-11 w-full rounded-lg bg-white px-3 font-mono text-sm font-bold uppercase tracking-wider text-ocean-950 outline-none placeholder:normal-case placeholder:font-sans placeholder:font-normal placeholder:text-ocean-800/50 focus:ring-2 focus:ring-teal-600 dark:bg-ocean-900 dark:text-foam-50"
                  />
                </div>
                <button
                  type="button"
                  onClick={applyReferral}
                  disabled={referralReady || referralInput.trim() === ""}
                  className="h-11 rounded-lg bg-ocean-800 px-5 text-sm font-bold text-white hover:bg-ocean-700 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                >
                  Apply
                </button>
              </div>
              <div id="profile-referral-message" role="status">
                {referralReady && (
                  <p className="flex items-center gap-2 rounded-lg bg-teal-200 px-3 py-2.5 text-sm font-semibold text-teal-700">
                    <CircleCheck className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span className="flex-1">Code {referralInput.trim().toUpperCase()} will be applied when you continue.</span>
                    <button
                      type="button"
                      aria-label="Remove referral code"
                      onClick={() => {
                        setReferralReady(false);
                        setReferralInput("");
                      }}
                      className="p-1 hover:opacity-80"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </p>
                )}
                {referralError && (
                  <p className="flex items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300">
                    <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {referralError}
                  </p>
                )}
              </div>
              {ownCode && (
                <div className="flex items-center justify-between gap-3 border-t border-ocean-900/10 pt-3 text-sm dark:border-white/10">
                  <p>
                    Your own code: <span className="font-mono font-bold tracking-wider">{ownCode}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => copyOwnCode(ownCode)}
                    className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-white px-3 text-xs font-bold text-teal-700 hover:bg-foam-100 dark:bg-ocean-900 dark:text-teal-300"
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              )}
            </section>

            <p className="flex items-start gap-3 rounded-lg bg-ocean-700/5 p-3 text-xs leading-snug text-ocean-800/85 dark:bg-white/10 dark:text-foam-100/80">
              <ShieldCheck className="h-6 w-6 shrink-0 text-teal-700 dark:text-teal-300" aria-hidden="true" />
              Your details travel over HTTPS and are stored in ZenFresh&apos;s account database. We use them for delivery and order support.
            </p>

            <div className="fixed inset-x-0 bottom-0 z-40 bg-foam-50/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] backdrop-blur-xl dark:bg-ocean-950/95">
              <div className="mx-auto flex max-w-xl flex-col items-center gap-1.5">
                <p role="alert" className="text-center text-sm text-coral-600 dark:text-coral-300">{formError}</p>
                <button
                  type="submit"
                  aria-disabled={saving}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ocean-800 text-sm font-bold text-white shadow-md transition-colors hover:bg-ocean-700 aria-disabled:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                >
                  {saving ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : null}
                  {saving ? "Saving…" : "Save and continue"}
                  {!saving && <ArrowRight className="h-5 w-5" aria-hidden="true" />}
                </button>
                <button
                  type="button"
                  onClick={onward}
                  className="min-h-9 px-2 text-xs font-semibold text-ocean-800 hover:text-teal-700 dark:text-foam-100"
                >
                  I&apos;ll finish my profile later
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </PageTransition>
  );
}
